import { useEffect, useRef } from 'react';
import { PanelResizeHandle, type PanelSize } from '../common/PanelResizeHandle';

interface ProofEditorProps {
  containerId: string;
  isLoaded?: boolean;
  isReady: boolean;
  initializationError?: string;
  onRetry: () => void;
  onResize: (size: PanelSize) => void;
}

export function ProofEditor({ 
  containerId, 
  isLoaded = true,
  isReady,
  initializationError,
  onRetry,
  onResize,
}: ProofEditorProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Ensure the container has the correct ID
    if (containerRef.current) {
      containerRef.current.id = containerId;
    }
  }, [containerId]);

  return (
    <div ref={panelRef} className="relative flex h-full min-h-0 flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between gap-2 shrink-0 bg-gray-50 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Proof Editor</h3>
      </div>
      {!isReady && <div role={initializationError ? 'alert' : 'status'} className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-primary-50 px-4 py-2 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
        <span>{initializationError || 'Starting Rocq and loading libraries… You can edit; stepping becomes available when Rocq is ready.'}</span>
        {initializationError && <button type="button" onClick={onRetry} className="rounded border border-gray-300 bg-white px-3 py-1 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 dark:border-gray-500 dark:bg-gray-800">Retry Rocq</button>}
      </div>}
      {/* jsCoq will create its own UI here with editor and goal panel */}
      {/* Don't use overflow-hidden as it might hide jsCoq's goal panel */}
      <div 
        ref={containerRef}
        id={containerId}
        className="proof-editor-host min-h-0 flex-1 w-full"
        aria-busy={!isReady}
      >
        {!isLoaded && (
          <div className="flex h-full items-center justify-center">
            <div className="text-gray-500 dark:text-gray-400">Loading jsCoq editor...</div>
          </div>
        )}
      </div>
      <PanelResizeHandle label="Resize proof editor" panelRef={panelRef} onResize={onResize} />
    </div>
  );
}
